import { faPlay } from "@fortawesome/pro-solid-svg-icons/faPlay";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Stack, useTheme } from "@mui/material";
import { AppImages } from "app/appImages";
import { MprcButton, MprcRequestErrorReport, MprcSpinnerIndicator } from "components";
import { MprcFeedTypography } from "components/MprcFeedTypography";
import { FeedContentLoadStatus } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router";
import { KnowledgePaths } from "routes/KnowledgeRouter";
import { useCardContext } from "views/knowledge/components/card/CardBuilder";
import { StickerBox } from "../common/StickerBox";

///
export const DefaultTitle = () => {
    const { pub: { interaction, rejected, blocked, publicationId, publicationType, title, sticker, availableInTheFuture, expired, viewPeriodStart } } = useCardContext();
    const theme = useTheme();
    const { t } = useTranslation("knowledge");
    const navigate = useNavigate();
    const location = useLocation();

    const isBlocked = blocked?.blockedOn != undefined;

    const color = isBlocked
        ? theme.palette.warning.main
        : expired || availableInTheFuture
            ? theme.palette.muted.light
            : theme.palette.common.black;

    var borderStyle: React.CSSProperties | undefined = (interaction.isNewPublication || blocked?.firstAccessAfterBlock === true || rejected?.firstAccessAfterRejected === true)
        ? { borderLeft: "4px solid", borderLeftColor: theme.palette.warning.main, paddingLeft: theme.spacing(1) }
        : undefined;

    return (
        <div style={borderStyle}>
            <Link
                onClick={() => navigate(document.location.search, { replace: true, state: { ...(location.state ?? {}), publicationId: publicationId } })}
                to={KnowledgePaths.PublicationViewerResolver(publicationId, publicationType)}
            >
                <Box sx={{ color }}>
                    <MprcFeedTypography variant="title" component="div" noWrap={true} color="inherit">
                        <span title={title}>{title}</span>
                    </MprcFeedTypography>
                </Box>
            </Link>

            <Stack spacing={0.5} alignItems="flex-end" direction="row">
                {isBlocked && <StickerBox name={t("Feed.Card.Common.BlockedText")} textColor={theme.palette.common.white} backgroundColor={theme.palette.warning.main} />}
                {expired && <StickerBox name={t("Feed.Card.Common.ExpiredText")} textColor={theme.palette.common.white} backgroundColor={theme.palette.muted.light} />}
                {availableInTheFuture && <StickerBox name={t("Feed.Card.Common.DisplayOnText", { date: Formaters.dayMonth(viewPeriodStart) })} textColor={theme.palette.common.white} backgroundColor={theme.palette.muted.light} />}
                {sticker && <StickerBox name={sticker.name} textColor={sticker.textColor} backgroundColor={sticker.backgroundColor} />}
            </Stack>
        </div>
    );
};


///
export const PublishedThumbnail = () => {
    const { pub: { listDefinition } } = useCardContext();

    const thumbnailUrl = listDefinition ? listDefinition.thumbnailUrl : AppImages.KnowledgeListThumbnail;

    return (
        <Box sx={{ width: 82, height: 48 }}>
            <img src={thumbnailUrl} width="100%" height="100%" style={{ objectFit: "contain", borderRadius: "6px" }} />
        </Box>
    );
};


///
export const DefaultContent = () => {
    const {
        pub: { publicationId, content },
        localContent,
        onUpdateLocalContent,
    } = useCardContext();

    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<any>(undefined);
    const { t } = useTranslation("knowledge");
    const api = useCoreApi();

    const loadContent = () => {
        setLoading(true);
        api.v1.knowledgePublication.getContent({ publicationId: publicationId })
            .then((result) => {
                onUpdateLocalContent({
                    content: result.data,
                    contentLoadStatus: FeedContentLoadStatus.AllContentLoaded,
                    state: result.data != null ? "expanded" : undefined
                });

            })
            .catch((e) => setError(e))
            .finally(() => setLoading(false));
    };

    const toogleState = () => onUpdateLocalContent({ ...localContent, state: localContent.state == "expanded" ? "contracted" : "expanded" });

    return error
        ? <MprcRequestErrorReport error={error} />
        : (
            <section id="k-content">
                {
                    localContent.contentLoadStatus == FeedContentLoadStatus.AllContentLoaded &&
                    <>
                        <MprcFeedTypography variant="content" component="div" className="ql-editor">
                            <div dangerouslySetInnerHTML={{ __html: localContent.state == "expanded" ? localContent.content! : content! }}></div>
                        </MprcFeedTypography>
                        <MprcFeedTypography variant="moreContent" component="a" onClick={toogleState}>
                            {localContent.state == "expanded" ? t("Feed.Card.Common.SeeLessText") : t("Feed.Card.Common.SeeMoreText")}
                        </MprcFeedTypography>
                    </>
                }

                {
                    localContent.contentLoadStatus == FeedContentLoadStatus.LoadContentInDemand &&
                    <>
                        <MprcFeedTypography variant="content" component="div" className="ql-editor">
                            <div dangerouslySetInnerHTML={{ __html: content ?? "" }}></div>
                        </MprcFeedTypography>
                        {
                            loading
                                ? <MprcSpinnerIndicator />
                                : <MprcFeedTypography variant="moreContent" component="a" onClick={() => loadContent()}>
                                    {t("Feed.Card.Common.SeeMoreText")}
                                </MprcFeedTypography>
                        }
                    </>
                }

                {
                    localContent.contentLoadStatus == FeedContentLoadStatus.NoActionNecessary &&
                    <>
                        <MprcFeedTypography variant="content" component="div" className="ql-editor">
                            <div dangerouslySetInnerHTML={{ __html: content ?? "" }}></div>
                        </MprcFeedTypography>
                    </>
                }
            </section>
        );
};

export const PlaybackButton = () => {
    const { pub: { publicationId, listDefinition } } = useCardContext();
    const { t } = useTranslation("knowledge");

    if (listDefinition?.itemCount == 0) return <></>;

    return (
        <Link to={`${KnowledgePaths.ListPublicationInPlayback}/${publicationId}`}>
            <MprcButton
                text={t("Feed.Card.List.PlaybackButtonText")}
                title={t("Feed.Card.List.PlaybackButtonTitle")}
                disabled={listDefinition?.itemCount == 0}
                startIcon={<FontAwesomeIcon icon={faPlay} />}
                variant="contained"
            />
        </Link>
    );
};

export const SeeListButton = () => {
    const { pub: { publicationId } } = useCardContext();
    const { t } = useTranslation("knowledge");

    return (
        <Link to={KnowledgePaths.FeedList(publicationId)}>
            <MprcButton
                text={t("Feed.Card.List.SeeListButtonText")}
                title={t("Feed.Card.List.SeeListButtonTitle")}
                variant="contained"
            />
        </Link>
    );
};
